import { useMemo, useState } from 'react';
import { tx } from '../i18n';
import { Link } from 'react-router';
import { useConsulta } from '../lib/consulta';
import { cuando } from '../lib/formato';
import type { Bitacora as Linea } from '../tipos';
import { Cabecera, Consulta, Tabla, Vacio } from '../ui/piezas';

/**
 * Todo lo que una persona hace en el Centro queda acá (centro.md §8). El filtro por acción
 * se arma con las acciones que de verdad aparecieron: no hay una lista fija que envejezca.
 */
/**
 * Lo que la API guarda en `detalle` es el pedido tal cual (jsonb): puede ser un objeto
 * (`{ip: "…"}`), un texto o nada. Acá se muestra legible — pintar el objeto directo hacía
 * explotar React.
 */
export function textoDetalle(d: unknown): string {
  if (d === null || d === undefined || d === '') return '—';
  if (typeof d === 'string') return d;
  if (typeof d !== 'object') return String(d);
  const partes = Object.entries(d as Record<string, unknown>).map(
    ([k, v]) => `${k}: ${v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`,
  );
  return partes.length ? partes.join(' · ') : '—';
}

export function Bitacora() {
  const [accion, setAccion] = useState('');
  const lista = useConsulta<Linea[]>('/bitacora', { refetchInterval: 60_000 });
  const lineas = useMemo(() => lista.data ?? [], [lista.data]);
  const acciones = useMemo(() => [...new Set(lineas.map((l) => l.accion))].sort(), [lineas]);
  const filtradas = accion ? lineas.filter((l) => l.accion === accion) : lineas;

  return (
    <div className="pagina">
      <Cabecera titulo={tx.bitacora.titulo}>{tx.bitacora.que}</Cabecera>

      <div className="filtros">
        <label className="solo-lector" htmlFor="f-accion">
          {tx.bitacora.accion}
        </label>
        <select id="f-accion" className="lista-sel" value={accion} onChange={(e) => setAccion(e.target.value)}>
          <option value="">{tx.bitacora.todasLasAcciones}</option>
          {acciones.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      </div>

      <section className="panel">
        <Consulta
          q={lista}
          vacio={lista.data && filtradas.length === 0 ? <Vacio>{accion ? tx.bitacora.nadaConEsaAccion : tx.bitacora.nadaTodavia}</Vacio> : undefined}
        >
          <div className="scroll-x">
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.bitacora.cuando}</th>
                  <th>{tx.bitacora.quien}</th>
                  <th>{tx.bitacora.accion}</th>
                  <th>{tx.bitacora.detalle}</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((l, i) => (
                  <tr key={`${l.tiempo}-${i}`}>
                    <td className="d num">{cuando(l.tiempo)}</td>
                    <td>{l.usuario}</td>
                    <td>
                      <span className="chip">{l.accion}</span>
                    </td>
                    <td className="d notas-celda">
                      {textoDetalle(l.detalle)}
                      {/* el objeto puede ser un appliance o una organización: se muestra el id,
                          y solo se enlaza cuando la acción dice que es de un appliance */}
                      {l.objeto_id && (
                        <small className="num">
                          {l.accion.startsWith('appliance') ? <Link to={`/appliances/${l.objeto_id}`}>{l.objeto_id}</Link> : l.objeto_id}
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </div>
        </Consulta>
      </section>
    </div>
  );
}
